"use client";

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useThree } from "@react-three/fiber";
import { PerspectiveCamera, Vector3 } from "three";
import { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { OrbitControls } from "@react-three/drei";
import { gsap } from "gsap";
import { ControlSettings, PageType } from "@/types";

const AnimatedCamera = forwardRef(({ onChange, page }: { onChange: () => void; page: PageType }, ref) => {
	const { camera } = useThree();
	const controls = useRef<OrbitControlsImpl | null>(null);
	const [inTransition, setInTransition] = useState<boolean>(false);

	const controlSettings: ControlSettings = {
		min_horizontal: -Infinity,
		max_horizontal: Infinity,
		min_vertical: -Infinity,
		max_vertical: Infinity,
		min_distance: 0,
		max_distance: Infinity,
		useDistance: false,
		useVertical: false,
		useHorizontal: false,
	};

	// Falls die Controls überhaupt aktiv sind
	if (page.controls) {
		if (page.controls.useDistance) {
			controlSettings.min_distance = page.controls.min_distance;
			controlSettings.max_distance = page.controls.max_distance;
		}
		if (page.controls.useVertical) {
			controlSettings.min_vertical = page.controls.min_vertical;
			controlSettings.max_vertical = page.controls.max_vertical;
		}

		if (page.controls.useHorizontal) {
			controlSettings.min_horizontal = page.controls.min_horizontal;
			controlSettings.max_horizontal = page.controls.max_horizontal;
		}
	}

	useImperativeHandle(ref, () => ({
		getControls() {
			return controls.current;
		},
		getCameraAndTarget() {
			return {
				camera: camera.position,
				target: controls.current?.target,
			};
		},
		moveToPosition(targetPosition: Vector3, targetLookAt: Vector3, cb?: () => void) {
			setInTransition(true);
			// console.log("animate Camera Position", camera, controls);
			const internalDuration = 2;

			if (!controls.current) {
				return;
			}
			// Animate camera position
			gsap.to(controls.current.object.position, {
				ease: "sine.inOut",
				x: targetPosition.x,
				y: targetPosition.y,
				z: targetPosition.z,
				duration: internalDuration,
				onUpdate: () => {
					if (!controls?.current) {
						return;
					}
					const lookAtVector = new Vector3(targetLookAt.x, targetLookAt.y, targetLookAt.z);
					controls.current.target.lerp(lookAtVector, 0.1);
					controls.current.update();
				},
				onComplete: () => {
					if (cb) {
						cb();
					}
					setInTransition(false);
				},
			});
		},
	}));

	useEffect(() => {
		if (controls.current) {
			// console.log("currentCamera", controls.current.object);
			(controls.current.object as PerspectiveCamera).fov = 40;
			(controls.current.object as PerspectiveCamera).updateProjectionMatrix();
		}
	}, [controls]);

	const handleChange = () => {
		if (!inTransition) {
			// console.log({
			// 	camera: camera.position,
			// 	target: controls.current?.target,
			// });
			onChange();
		}
	};

	return (
		<>
			<OrbitControls
				makeDefault
				ref={controls}
				minDistance={controlSettings.min_distance}
				maxDistance={controlSettings.max_distance}
				enabled={!inTransition}
				dampingFactor={0.1}
				onChange={handleChange}
				minAzimuthAngle={(controlSettings.min_horizontal * Math.PI) / 180}
				maxAzimuthAngle={(controlSettings.max_horizontal * Math.PI) / 180}
				minPolarAngle={(controlSettings.min_vertical * Math.PI) / 180}
				maxPolarAngle={(controlSettings.max_vertical * Math.PI) / 180}
			/>
		</>
	);
});

// Setze den displayName für die Komponente
AnimatedCamera.displayName = "AnimatedCamera";

export default AnimatedCamera;
